<!-- components/ContactForm.vue -->
<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import axios from 'axios'
import PhoneInput from '@/components/PhoneInput.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  initialName: {
    type: String,
    default: '',
  },
})


const emit = defineEmits(['update:modelValue', 'created'])

const { t } = useI18n()
const { success, error } = useSnackbar()
const loading = ref(false)

// Form state
const form = ref({
  name: props.initialName,
  phone_prefix: '+261',
  phone: '',
  secondary_phone_prefix: '+261',
  secondary_phone: '',
  email: '',
  address: '',
  type: 'customer',
})

// Phone validation functions
const validatePhone = (phoneNumber, prefix) => {
  const number = phoneNumber.replace(/\s/g, '')
  if (!number) return prefix === form.value.phone_prefix ? false : true // Only primary phone is required

  if (prefix === '+261') {
    return number.length === 9
  } else if (prefix === '+86') {
    return number.length === 11
  }

  return false
}

// Error state
const errors = ref({
  name: '',
  phone: '',
  secondary_phone: '',
  email: '',
})

// Form validation
const validateEmail = email => {
  if (!email) return true // Optional field
  const re = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/

  return re.test(email)
}


// Form validation
const validate = () => {
  errors.value = {}

  if (!form.value.name?.trim()) {
    errors.value.name = t('validation.nameRequired')
  }

  if (!validatePhone(form.value.phone, form.value.phone_prefix)) {
    errors.value.phone = form.value.phone_prefix === '+261'
      ? t('validation.phoneMadagascar')
      : t('validation.phoneChina')
  }

  // Only validate secondary phone if it's provided
  if (form.value.secondary_phone && !validatePhone(form.value.secondary_phone, form.value.secondary_phone_prefix)) {
    errors.value.secondary_phone = form.value.secondary_phone_prefix === '+261'
      ? t('validation.phoneMadagascar')
      : t('validation.phoneChina')
  }

  if (form.value.email && !validateEmail(form.value.email)) {
    errors.value.email = t('validation.invalidEmail')
  }

  return Object.keys(errors.value).length === 0
}

axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL
axios.defaults.headers.common['Accept'] = 'application/json'

// Form submission
const submitForm = async () => {
  if (!validate()) return

  loading.value = true
  try {
    const formData = {
      name: form.value.name.trim(),
      phone: form.value.phone_prefix + form.value.phone.replace(/\s/g, ''),
      secondary_phone: form.value.secondary_phone ? form.value.secondary_phone_prefix + form.value.secondary_phone.replace(/\s/g, '') : null,
      email: form.value.email.trim(),
      address: form.value.address.trim(),
      type: 'customer',
    }

    // Get access token from localStorage instead of useCookie
    const accessToken = localStorage.getItem('accessToken')

    const response = await axios.post('/shipment/customers', formData, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })

    // Handle success
    const formattedCustomer = {
      id: response.data.data.id,
      text: response.data.data.phone
        ? `${response.data.data.name} (${response.data.data.phone})`
        : response.data.data.name,
      name: response.data.data.name,
      phone: response.data.data.phone,
    }

    emit('created', formattedCustomer)
    emit('update:modelValue', false)
    success(t('messages.customerCreated'))

  } catch (err) {
    console.error('❌ Submit error:', err)

    // Handle validation errors (422)
    if (err.response?.status === 422) {
      const validationErrors = err.response.data.errors
      if (validationErrors) {
        Object.entries(validationErrors).forEach(([field, messages]) => {
          errors.value[field] = messages[0]
        })
      }

      return
    }

    // Handle other errors
    error(t('messages.createError'))
  } finally {
    loading.value = false
  }
}


const close = () => {
  emit('update:modelValue', false)
}

watch(() => props.initialName, newValue => {
  form.value.name = newValue
})

const handleFormSubmit = e => {
  e.preventDefault()
  submitForm()
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="600px"
    persistent
    @update:model-value="close"
  >
    <VCard>
      <VCardTitle class="pt-3 px-4">
        {{ t('contact.createCustomer') }}
      </VCardTitle>

      <VCardText>
        <VForm @submit.prevent="submitForm">
          <VRow>
            <!-- Name -->
            <VCol cols="12">
              <VTextField
                v-model="form.name"
                :label="t('contact.name')"
                :error-messages="errors.name"
                required
                density="comfortable"
                variant="outlined"
                autofocus
              />
            </VCol>

            <!-- Phone -->
            <VCol cols="12">
              <PhoneInput
                v-model="form.phone"
                v-model:prefix="form.phone_prefix"
                :label="t('contact.phone')"
                :error-messages="errors.phone"
                required
                density="comfortable"
                variant="outlined"
              />
            </VCol>

            <!-- Secondary Phone -->
            <VCol cols="12">
              <PhoneInput
                v-model="form.secondary_phone"
                v-model:prefix="form.secondary_phone_prefix"
                :label="t('contact.secondaryPhone') || 'Secondary Phone'"
                :error-messages="errors.secondary_phone"
                density="comfortable"
                variant="outlined"
              />
            </VCol>

            <!-- Email -->
            <VCol cols="12">
              <VTextField
                v-model="form.email"
                :label="t('contact.email')"
                :error-messages="errors.email"
                type="email"
                density="comfortable"
                variant="outlined"
              />
            </VCol>

            <!-- Address -->
            <VCol cols="12">
              <VTextarea
                v-model="form.address"
                :label="t('contact.address')"
                :error-messages="errors.address"
                density="comfortable"
                variant="outlined"
                rows="3"
                auto-grow
              />
            </VCol>
          </VRow>
        </VForm>
      </VCardText>

      <VCardActions class="pt-0 px-4 pb-4">
        <VSpacer />
        <VBtn
          color="secondary"
          variant="outlined"
          :disabled="loading"
          @click="close"
        >
          {{ t('actions.cancel') }}
        </VBtn>

        <VBtn
          color="primary"
          class="ml-3"
          :loading="loading"
          @click="handleFormSubmit"
        >
          {{ t('actions.create') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
